/**
 * StepPane (BLU-1376, handover 4.1): the single step card of layout B.
 *
 * Top to bottom: the eyebrow ("Step N of M · Group"), a 30px badge, the title
 * (an `h2` the page can focus when the step becomes selected), the description,
 * the status pill, a saved summary box whenever the step is complete (so a
 * finished step's result never vanishes), the body, then ONE footer row.
 *
 * The frame (number, state, footer actions, glow, heading ref) comes from
 * `useStepChrome()`; the step body only passes its own title, copy and content.
 *
 * Card by state (handover 4.2): current = purple border + 1px ring + lift
 * shadow; locked = dashed border on a transparent card, and the body is
 * replaced by a note pointing at the step to finish first (the body is never
 * mounted, so a locked step fires none of its queries); everything else = a
 * plain line-strong border.
 */
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Ban, Check, Clock, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { StepBadge } from './StepBadge';
import { StepStatusPill } from './StepStatusPill';
import { StepFooter } from './StepFooter';
import { useStepChrome } from './StepChrome';

interface StepPaneProps {
  title: ReactNode;
  description?: ReactNode;
  /** What the finished step saved. Shown in the saved box once complete. */
  summary?: ReactNode;
  children?: ReactNode;
  /** Left side of the footer row (a short note on what the primary needs). */
  footerHint?: ReactNode;
  /** Right side of the footer row: the step's primary action. */
  primary?: ReactNode;
  className?: string;
}

const SAVED_BOX: Record<'done' | 'skipped' | 'waiting', { className: string; Icon: typeof Check }> = {
  done: { className: 'border-emerald-200 bg-emerald-50 text-emerald-900', Icon: Check },
  skipped: { className: 'border-dashed border-line-strong bg-muted/50 text-muted-foreground', Icon: Ban },
  waiting: { className: 'border-amber-200 bg-amber-50 text-amber-900', Icon: Clock },
};

export function StepPane({ title, description, summary, children, footerHint, primary, className }: StepPaneProps) {
  const { t } = useTranslation('onboarding');
  const chrome = useStepChrome();
  const headingId = useId();
  const { state } = chrome;
  const locked = state === 'locked';
  const savedBox = state === 'done' || state === 'skipped' || state === 'waiting' ? SAVED_BOX[state] : null;

  return (
    <section
      aria-labelledby={headingId}
      data-state={state}
      data-step={chrome.stepId}
      data-glow={chrome.glow ? 'true' : undefined}
      className={cn(
        'rounded-xl p-5 sm:p-6',
        'transition-[border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none',
        state === 'current'
          ? 'border border-threadheads-purple bg-card shadow-md ring-1 ring-threadheads-purple'
          : locked
            ? 'border border-dashed border-line-strong bg-transparent'
            : 'border border-line-strong bg-card',
        // One animation at a time: the unlock glow wins over the pane fade-in.
        chrome.glow ? 'motion-safe:animate-unlock-glow' : 'motion-safe:animate-fade-in',
        className,
      )}
    >
      <header className="flex items-start gap-3">
        <StepBadge number={chrome.number} state={state} size="pane" className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase leading-4 tracking-wide text-muted-foreground">
            {chrome.optional
              ? t('shell.eyebrowOptional', { group: chrome.groupLabel })
              : t('shell.eyebrow', { number: chrome.number, total: chrome.total, group: chrome.groupLabel })}
          </p>
          <div className="mt-1 flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
            <h2
              id={headingId}
              ref={chrome.headingRef}
              tabIndex={-1}
              className={cn(
                'min-w-0 text-[19px] font-semibold leading-7 focus:outline-none',
                locked ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {title}
            </h2>
            <StepStatusPill state={state} className="mt-0.5" />
          </div>
          {description != null && (
            <p className="mt-1 max-w-prose text-sm leading-5 text-muted-foreground">{description}</p>
          )}
        </div>
      </header>

      {savedBox && summary != null && (
        <div
          role="status"
          className={cn('mt-4 flex items-start gap-2 rounded-lg border px-3.5 py-2.5 text-sm', savedBox.className)}
        >
          <savedBox.Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden="true" />
          <div className="min-w-0">{summary}</div>
        </div>
      )}

      {locked ? (
        // The page always names the step to finish first; without it there is
        // nothing useful to say, so the pill alone carries the state.
        chrome.lockedTarget && (
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-lg bg-muted/60 px-4 py-3">
            <Lock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p className="min-w-0 flex-1 text-sm text-muted-foreground">
              {t('shell.locked.note', { label: chrome.lockedTarget.label })}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 px-3 text-xs"
              onClick={chrome.lockedTarget.onGo}
            >
              {t('shell.locked.go', { label: chrome.lockedTarget.label })}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        )
      ) : (
        <>
          {children != null && <div className="mt-5">{children}</div>}
          <StepFooter hint={footerHint} primary={primary} />
        </>
      )}
    </section>
  );
}
